import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import QueryBuilderIcon from '@material-ui/icons/QueryBuilder';

import { translate } from 'services/i18nService';
import ControlledFeatureService from 'services/ControlledFeatureService';
import PrivateShowRatingService from 'services/PrivateShowRatingService';
import ModalService from 'services/ModalService';
import ChatStore from 'stores/ChatStore';
import GiphyStore from 'stores/GiphyStore';
import ProfileStore from 'stores/ProfileStore';
import UserStore from 'stores/UserStore';
import FirebaseStore from 'stores/FirebaseStore';
import { CHAT_AVATAR_URL } from 'containers/Chat/Api';
import BottomDrawer from 'components/BottomDrawer/BottomDrawer';
import PostShowTip from 'components/PostShowTip';
import Button from 'components/Button/Button';
import MultipleStarRating from 'components/MultipleStarRating/MultipleStarRating';
import SendQuickGift from 'components/SendQuickGiftModal/SendQuickGift';
import { SECOND_AS_MILLISECONDS } from 'enum/timeFormat';
import allUserGroups from 'enum/userGroups';
import { BROADCAST_STATUS } from 'enum/videoPlayer';
import { generateID, msToTime } from 'utils/helpers';
import useResize from 'hooks/useResize';

import styles from './MobilePrivateShowEnded.css';


const IDS = {
  prefix: 'HTTP404E',
  element: 'MobilePrivateShowEnded',
  skip: 'skip',
};
const makeId = generateID(IDS.prefix, IDS.element);

const SMALL_DESKTOP_HEIGHT_THRESHOLD = 800;
const SMALL_DESKTOP_WIDTH_THRESHOLD = 1280;

const MobilePrivateShowEnded = (props) => {
  const {
    embedProfileV2,
    isDesktop,
    isPublicShow,
    isSpyingShow,
    showBuyTokensModal,
    user,
  } = props;

  const {
    setProfileChatTabValue,
    togglePrivateMessages,
  } = ChatStore;

  const {
    performerDetails,
    privateShowStartTime,
    setBroadcastStatus,
    setPublicShowEnded,
    setSpyShowEnded,
    spyShowStartTime,
  } = ProfileStore;

  const { broadcastStartTime } = FirebaseStore;

  const { username: performerName } = performerDetails || {};

  const { user: { username, accessHash } } = UserStore;

  const [showRating, setShowRating] = useState(null);
  const [timerId, setTimerId] = useState(null);
  const [showTime, setShowTime] = useState(null);
  const [quickGiftsOpened, setQuickGiftsOpened] = useState(false);

  const isPremiumPrivate = ControlledFeatureService.setUser(user).has(allUserGroups.PREMIUM_PRIVATE_SHOW);
  const privateShowRating = ControlledFeatureService.setUser(user).has(allUserGroups.PRIVATE_SHOW_RATINGS);

  useEffect(
    () => () => {
      clearTimeout(timerId);
    },
    [timerId]
  );

  useEffect(() => {
    const { closePopper } = GiphyStore;

    const startShowTime = isSpyingShow ? spyShowStartTime : privateShowStartTime;
    const now = Date.now();
    const privateShowLengthMiliseconds = now - startShowTime;
    const privateShowLength = msToTime(privateShowLengthMiliseconds);

    setShowTime(privateShowLength);

    closePopper();
  }, [
    isSpyingShow,
    privateShowStartTime,
    spyShowStartTime,
  ]);

  const handleRating = useCallback((rate) => {
    setShowRating(rate);
    if (privateShowRating) {
      const {
        getPerformerRating,
        addPerformerRating,
        updatePerformerRating,
      } = PrivateShowRatingService;

      getPerformerRating(performerName, username, 'PERFORMER', accessHash)
        .then((data) => {
          if (data.length === 0) {
            addPerformerRating(performerName, username, 'PERFORMER', accessHash, rate);
          } else {
            const ratingId = data[0].id;
            updatePerformerRating(performerName, username, accessHash, ratingId, rate);
          }
        });
    }

    // Rating less then 4 stars will move user to next performer state
    // Rating of 4 stars or more will show second stage for sending gifts
    if (rate < 4 || embedProfileV2) {
      const id = setTimeout(() => {
        setBroadcastStatus(BROADCAST_STATUS.DISCONNECTED);
      }, SECOND_AS_MILLISECONDS);
      setTimerId(id);
    }
  }, [
    accessHash,
    embedProfileV2,
    performerName,
    privateShowRating,
    setBroadcastStatus,
    username,
  ]);

  const handleSkip = useCallback(() => {
    if (isPublicShow) {
      setPublicShowEnded(false);
      setBroadcastStatus(BROADCAST_STATUS.DISCONNECTED);
    }

    if (isPremiumPrivate && !showRating && !isSpyingShow) {
      setShowRating(0);
      setBroadcastStatus(BROADCAST_STATUS.DISCONNECTED);
    } else {
      if (isSpyingShow) {
        setSpyShowEnded(false);
      }

      setBroadcastStatus(BROADCAST_STATUS.DISCONNECTED);

      if (isDesktop) {
        setProfileChatTabValue(0);
        togglePrivateMessages(false);
      }
    }
  }, [
    isDesktop,
    isPremiumPrivate,
    isPublicShow,
    isSpyingShow,
    setBroadcastStatus,
    setProfileChatTabValue,
    setPublicShowEnded,
    setSpyShowEnded,
    showRating,
    togglePrivateMessages,
  ]);

  const openBuyGiftsModal = useCallback(() => {
    if (!isDesktop) {
      setQuickGiftsOpened(true);

      return;
    }

    ModalService.showModal(
      ModalService.MODAL_KEYS.SEND_QUICK_GIFT_MODAL,
      { handleSkip }
    );
  }, [handleSkip, isDesktop]);

  const closeQuickGifts = useCallback(() => {
    setQuickGiftsOpened(false);
  }, [setQuickGiftsOpened]);

  const [width, height] = useResize();

  const smallDesktopScreen = (
    isDesktop &&
    height < SMALL_DESKTOP_HEIGHT_THRESHOLD &&
    width < SMALL_DESKTOP_WIDTH_THRESHOLD
  );

  const showStepOne = isPremiumPrivate && !isSpyingShow;
  const showStepTwo = !showStepOne || showRating > 3;

  const wrapperClass = classNames({
    [styles.offlineWrapper]: !isDesktop,
    [styles.offlineWrapperDesktop]: isDesktop,
    [styles.smallDesktopScreen]: smallDesktopScreen,
  });

  const broadcastDuration = useMemo(() => {
    let duration = undefined;
    if (!isPublicShow) {
      duration = showTime;
    } else {
      const currentMS = Date.now();
      if (isNaN(broadcastStartTime)) {
        return null;
      }

      const publicShowLengthMS = currentMS - broadcastStartTime;
      duration = msToTime(publicShowLengthMS);
    }

    return (
      <div className={styles.broadcastDuration}>
        <QueryBuilderIcon className={styles.broadcastDurationLogo} />
        {duration}
      </div>
    );
  }, [broadcastStartTime, isPublicShow, showTime]);

  return (
    <div className={wrapperClass}>
      {!showStepTwo &&
        <div className={styles.performerInfoWrapper}>
          {!smallDesktopScreen && (
            <div className={styles.profileImageWrapper}>
              <img
                alt="performer"
                src={CHAT_AVATAR_URL.replace('{username}', performerName)}
              />
            </div>
          )}
          <h5>
            {translate('v2profile.show.hasEnded')}
          </h5>
          {broadcastDuration}
        </div>
      }

      {showStepOne && (
        <div className={showStepTwo ? `${styles.fadeout} ${styles.ratingWrap}` : styles.ratingWrap}>
          <p>{translate('v2profile.show.rateShow')}</p>
          <MultipleStarRating
            emptyStarProps={{ classes: { root: classNames(styles.starBase, styles.star) } }}
            filledStarProps={{ classes: { root: classNames(styles.starBase, styles.ratedStar) } }}
            onStarClick={handleRating}
          />
        </div>
      )}

      {showStepTwo && (
        <div className={`${styles.slidein} ${styles.giftsWrap}`}>
          <PostShowTip
            handleSkip={handleSkip}
            openBuyGiftsModal={openBuyGiftsModal}
            showBuyTokensModal={showBuyTokensModal}
          />
        </div>
      )}

      {!showStepTwo &&
        <div className={styles.skipWrap}>
          <Button
            id={makeId(IDS.skip)}
            label={translate('v2profile.button.skip')}
            onClick={handleSkip}
          />
        </div>
      }

      {!embedProfileV2 && (
        <BottomDrawer
          opened={quickGiftsOpened}
          onClose={closeQuickGifts}
        >
          <SendQuickGift
            close={closeQuickGifts}
            handleSkip={handleSkip}
          />
        </BottomDrawer>
      )}
    </div>
  );
};

MobilePrivateShowEnded.propTypes = {
  user: PropTypes.object.isRequired,
  embedProfileV2: PropTypes.bool,
  isDesktop: PropTypes.bool,
  isPublicShow: PropTypes.bool,
  isSpyingShow: PropTypes.bool,
  showBuyTokensModal: PropTypes.func,
};

MobilePrivateShowEnded.defaultProps = {
  user: PropTypes.object.isRequired,
  embedProfileV2: false,
  isDesktop: false,
  isPublicShow: false,
  isSpyingShow: false,
  showBuyTokensModal: () => undefined,
};

export default observer(MobilePrivateShowEnded);
